/**
 * GZOO AI Marketing - White-label BrandingProvider (Wave 6, Phase 1)
 *
 * Applies the active org's branding to the live document: the accent colour (via the
 * --brand-primary / --brand-accent CSS variables that drive `brand-orange` everywhere),
 * the browser tab title, and the favicon. Reverts cleanly to the GZOO defaults on logout
 * or for an org with no custom branding, so the unbranded experience is unchanged.
 *
 * Note (known, acceptable for Phase 1): a branded org shows a brief GZOO-orange accent
 * before /auth/me resolves (FOUC). Eddy's own org has no branding, so it never flashes.
 * Cookie-based SSR theming to remove the flash is a later refinement.
 */
'use client';

import { useEffect, type ReactNode } from 'react';
import { useBranding, GZOO_BRANDING } from '@/hooks/useBranding';
import { deriveDarkTheme } from '@/lib/brandTheme';

function setFavicon(href: string) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

// Inject (or clear) a <style> that overrides the dark-mode CSS vars for a branded background.
// Scoped to `.dark` so it only retones dark mode and never disturbs the light theme.
function setDarkThemeStyle(css: string | null) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById('gzoo-brand-dark') as HTMLStyleElement | null;
  if (!css) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = 'gzoo-brand-dark';
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const branding = useBranding();

  useEffect(() => {
    const root = document.documentElement;
    const primary = branding.primary_color || GZOO_BRANDING.primary_color;
    const accent = branding.accent_color || primary;
    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-accent', accent);

    // Brand background → a derived, contrast-safe dark palette (or revert to the GZOO theme
    // when the org has no custom/default background).
    const theme = deriveDarkTheme(branding.background_color);
    if (theme) {
      root.style.setProperty('--brand-bg', theme.brandBg);
      root.style.setProperty('--brand-surface', theme.brandSurface);
      setDarkThemeStyle(
        '.dark{' + Object.entries(theme.vars).map(([k, v]) => `${k}:${v};`).join('') + '}',
      );
    } else {
      root.style.removeProperty('--brand-bg');
      root.style.removeProperty('--brand-surface');
      setDarkThemeStyle(null);
    }

    if (branding.app_name) document.title = branding.app_name;
    setFavicon(branding.favicon_url || GZOO_BRANDING.favicon_url);
  }, [
    branding.primary_color,
    branding.accent_color,
    branding.background_color,
    branding.app_name,
    branding.favicon_url,
  ]);

  return <>{children}</>;
}

export default BrandingProvider;
